import { useState, useMemo, useReducer, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { TLeadTemperature, TLeadType, TLeadSortOption, IMockLead } from '@/@types/leads';
import { LeadsPageHeader } from '@/components/leads/LeadsPageHeader';
import { LeadFilterTabs } from '@/components/leads/LeadFilterTabs';
import { LeadSecondaryFilters } from '@/components/leads/LeadSecondaryFilters';
import { LeadTableHeader } from '@/components/leads/LeadTableHeader';
import { LeadTableRow } from '@/components/leads/LeadTableRow';
import { LeadBulkDeleteDialog } from '@/components/leads/LeadBulkDeleteDialog';
import { LeadTableSkeleton } from '@/components/leads/LeadTableSkeleton';
import { useLeadDeletion } from '@/hooks/useLeadDeletion';
import { RequestsCountCell } from '@/components/leads/RequestsCountCell';
import { cn } from '@/lib/utils';
import { Users, Trash2, Columns3 } from 'lucide-react';
import type { Id } from '../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: standard convex _generated import

// LOCAL_TYPE_JUSTIFIED: Reducer state/action types are internal to this file only
type LeadFilterState = {
  temperatureFilter: TLeadTemperature | 'all';
  typeFilter: TLeadType | 'all';
  searchQuery: string;
  sortBy: TLeadSortOption;
};

// LOCAL_TYPE_JUSTIFIED: Reducer action types are internal to this file only
type LeadFilterAction =
  | { type: 'SET_TEMPERATURE'; payload: TLeadTemperature | 'all' }
  | { type: 'SET_TYPE'; payload: TLeadType | 'all' }
  | { type: 'SET_SEARCH'; payload: string }
  | { type: 'SET_SORT'; payload: TLeadSortOption };

const initialLeadFilterState: LeadFilterState = {
  temperatureFilter: 'all',
  typeFilter: 'all',
  searchQuery: '',
  sortBy: 'last_contact',
};

function leadFilterReducer(state: LeadFilterState, action: LeadFilterAction): LeadFilterState {
  switch (action.type) {
    case 'SET_TEMPERATURE':
      return { ...state, temperatureFilter: action.payload };
    case 'SET_TYPE':
      return { ...state, typeFilter: action.payload };
    case 'SET_SEARCH':
      return { ...state, searchQuery: action.payload };
    case 'SET_SORT':
      return { ...state, sortBy: action.payload };
  }
}

export function LeadsPage() {
  const navigate = useNavigate();

  // Mutations
  const retriggerMessengerCheck = useMutation(api.leads.mutations.retriggerMessengerCheck);

  // Data
  const rawLeads = useQuery(api.leads.list, {});
  const isLoading = rawLeads === undefined;
  const leads: IMockLead[] = useMemo(() => rawLeads ?? [], [rawLeads]);

  // Filter state
  const [filters, dispatchFilter] = useReducer(leadFilterReducer, initialLeadFilterState);

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Column-visibility state — Requests column hidden by default
  const [showRequestsColumn, setShowRequestsColumn] = useState(false);

  // Deletion
  const { isDialogOpen, isDeleting, pendingCount, requestDelete, confirmDelete, cancelDelete } =
    useLeadDeletion(() => setSelectedIds(new Set()));

  // Calculate temperature counts
  const temperatureCounts = useMemo(() => {
    const counts = { all: leads.length, hot: 0, warm: 0, cold: 0 };
    leads.forEach(lead => {
      if (lead.temperature === 'hot') counts.hot++;
      else if (lead.temperature === 'warm') counts.warm++;
      else if (lead.temperature === 'cold') counts.cold++;
    });
    return counts;
  }, [leads]);

  // Filter and sort leads
  const filteredLeads = useMemo(() => {
    let result = [...leads];

    // Temperature filter
    if (filters.temperatureFilter !== 'all') {
      result = result.filter(lead => lead.temperature === filters.temperatureFilter);
    }

    // Type filter
    if (filters.typeFilter !== 'all') {
      result = result.filter(lead => lead.leadType === filters.typeFilter);
    }

    // Search filter
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      result = result.filter(lead => {
        const nameMatch = lead.name?.toLowerCase().includes(query) ?? false;
        const phoneMatch = lead.phoneNumber?.includes(query) ?? false;
        return nameMatch || phoneMatch;
      });
    }

    // Sort
    result.sort((a, b) => {
      switch (filters.sortBy) {
        case 'last_contact':
          return b.lastMessageAt - a.lastMessageAt;
        case 'name':
          return (a.name ?? '').localeCompare(b.name ?? '');
        case 'created':
          return b.createdAt - a.createdAt;
        default:
          return 0;
      }
    });

    return result;
  }, [leads, filters.temperatureFilter, filters.typeFilter, filters.searchQuery, filters.sortBy]);

  // Selection handlers
  const handleSelectAll = useCallback(
    (selected: boolean) => {
      if (selected) {
        setSelectedIds(new Set(filteredLeads.map(l => l._id)));
      } else {
        setSelectedIds(new Set());
      }
    },
    [filteredLeads]
  );

  const handleSelectLead = useCallback((leadId: string, selected: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (selected) {
        next.add(leadId);
      } else {
        next.delete(leadId);
      }
      return next;
    });
  }, []);

  // Action handlers
  const handleExport = useCallback(() => {
    // TODO: implement CSV/spreadsheet export for filteredLeads
  }, []);

  const handleAddLead = useCallback(() => {
    // TODO: open create-lead dialog
  }, []);

  const handleCall = useCallback((phoneNumber: string) => {
    window.location.href = `tel:${phoneNumber}`;
  }, []);

  const handleMessage = useCallback((_leadId: string) => {
    // TODO: open messaging panel for this lead
  }, []);

  const handleLeadClick = useCallback(
    (leadId: string) => {
      navigate(`/leads/${leadId}`);
    },
    [navigate]
  );

  const handleRetrigger = useCallback(
    (leadId: string) => {
      void retriggerMessengerCheck({ leadId: leadId as Id<'leads'> }); // AS_JUSTIFIED: route-level Id cast
    },
    [retriggerMessengerCheck]
  );

  // Computed selection state
  const allSelected = filteredLeads.length > 0 && filteredLeads.every(l => selectedIds.has(l._id));
  const someSelected = selectedIds.size > 0 && !allSelected;

  return (
    <div className="min-h-full bg-background-secondary py-8 px-10">
      {/* Page Header */}
      <LeadsPageHeader
        totalCount={leads.length}
        onExport={handleExport}
        onAddLead={handleAddLead}
      />

      {/* Table Container */}
      <div className="bg-table-container border border-table-container-border rounded-2xl overflow-hidden shadow">
        {/* Temperature Filter Tabs */}
        <LeadFilterTabs
          activeFilter={filters.temperatureFilter}
          counts={temperatureCounts}
          onFilterChange={val => dispatchFilter({ type: 'SET_TEMPERATURE', payload: val })}
        />

        {/* Secondary Filters */}
        <LeadSecondaryFilters
          typeFilter={filters.typeFilter}
          searchQuery={filters.searchQuery}
          sortBy={filters.sortBy}
          onTypeChange={val => dispatchFilter({ type: 'SET_TYPE', payload: val })}
          onSearchChange={val => dispatchFilter({ type: 'SET_SEARCH', payload: val })}
          onSortChange={val => dispatchFilter({ type: 'SET_SORT', payload: val })}
        />

        {/* Column-visibility toolbar */}
        <div className="flex items-center gap-2 px-6 py-2 border-b border-table-row-border">
          <button
            className={cn(
              'flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors',
              showRequestsColumn
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            )}
            onClick={() => setShowRequestsColumn(prev => !prev)}
          >
            <Columns3 className="w-3.5 h-3.5" /># Requests
          </button>
        </div>

        {/* Table */}
        <div className="overflow-auto">
          <LeadTableHeader
            allSelected={allSelected}
            someSelected={someSelected}
            onSelectAll={handleSelectAll}
            showRequestsColumn={showRequestsColumn}
          />

          {isLoading && <LeadTableSkeleton />}

          {!isLoading &&
            filteredLeads.map(lead => (
              <LeadTableRow
                key={lead._id}
                lead={lead}
                isSelected={selectedIds.has(lead._id)}
                onSelect={selected => handleSelectLead(lead._id, selected)}
                onClick={() => handleLeadClick(lead._id)}
                onCall={lead.phoneNumber ? () => handleCall(lead.phoneNumber ?? '') : undefined}
                onMessage={() => handleMessage(lead._id)}
                onDelete={() => requestDelete([lead._id as Id<'leads'>])} // AS_JUSTIFIED: route-level Id cast
                onRetrigger={lead.phoneNumber ? handleRetrigger : undefined}
                showRequestsColumn={showRequestsColumn}
                requestsCell={
                  showRequestsColumn ? (
                    <RequestsCountCell leadId={lead._id as Id<'leads'>} /> // AS_JUSTIFIED: lead._id from Convex list query, typed as leads table Id
                  ) : undefined
                }
              />
            ))}

          {/* Empty state */}
          {!isLoading && filteredLeads.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Users className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-1">No leads found</h3>
              <p className="text-sm text-muted-foreground">
                Try adjusting your filters or search query
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Floating selection action bar */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3 bg-card border border-border rounded-xl px-4 py-2.5 shadow-lg z-50">
          <span className="text-sm font-medium text-foreground">{selectedIds.size} selected</span>
          <button
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-destructive text-destructive-foreground text-sm font-medium hover:bg-destructive/90 transition-colors"
            onClick={
              () => requestDelete(Array.from(selectedIds) as Id<'leads'>[]) // AS_JUSTIFIED: selectedIds come from lead._id
            }
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      )}

      <LeadBulkDeleteDialog
        isOpen={isDialogOpen}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        isDeleting={isDeleting}
        count={pendingCount}
      />
    </div>
  );
}
